import { useState, useRef, useCallback } from "react";

/**
 * BeforeAfterSlider — a draggable comparison slider between two images.
 *
 * Props:
 *   beforeSrc / beforeLabel
 *   afterSrc / afterLabel
 *   aspect — Tailwind aspect class (default "aspect-[16/9]")
 *   testId — root data-testid
 */
export default function BeforeAfterSlider({
  beforeSrc,
  beforeLabel = "Before",
  afterSrc,
  afterLabel = "After",
  aspect = "aspect-[16/9]",
  testId = "before-after",
}) {
  const [pct, setPct] = useState(50);
  const wrapRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPct((x / rect.width) * 100);
  }, []);

  const onMouseDown = (e) => {
    draggingRef.current = true;
    updateFromClientX(e.clientX);
  };
  const onMouseMove = (e) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const onMouseUp = () => {
    draggingRef.current = false;
  };
  const onTouchMove = (e) => {
    if (!e.touches[0]) return;
    updateFromClientX(e.touches[0].clientX);
  };
  const onTouchStart = (e) => {
    if (!e.touches[0]) return;
    draggingRef.current = true;
    updateFromClientX(e.touches[0].clientX);
  };

  return (
    <div
      ref={wrapRef}
      className={`relative ${aspect} w-full overflow-hidden rounded-lg border border-white/10 bg-black select-none cursor-ew-resize`}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onMouseUp}
      data-testid={testId}
    >
      {/* AFTER image is the full base layer */}
      <img
        src={afterSrc}
        alt={afterLabel}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
        crossOrigin="anonymous"
      />
      {/* BEFORE image clipped from the left */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pct}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeLabel}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ width: `${(100 / Math.max(pct, 0.0001)) * 100}%` }}
          draggable={false}
          crossOrigin="anonymous"
        />
      </div>

      {/* Labels */}
      <span className="absolute top-3 left-3 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded pointer-events-none">
        {beforeLabel}
      </span>
      <span className="absolute top-3 right-3 bg-[#F5A524]/90 text-[#0A0A0A] text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded pointer-events-none">
        {afterLabel}
      </span>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white shadow-[0_0_8px_rgba(245,165,36,0.8)] pointer-events-none"
        style={{ left: `${pct}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white border-2 border-[#F5A524] shadow-lg flex items-center justify-center pointer-events-none"
        style={{ left: `${pct}%` }}
      >
        <span className="text-[#F5A524] font-bold text-sm leading-none">⇄</span>
      </div>
    </div>
  );
}
